import React, {useEffect, useRef, useState} from 'react'
import {Element, TypedElement} from '../../entities/project'
import {Tree, Button as AntdButton, Tabs, message, Col, Badge} from 'antd'
import type {DataNode, DirectoryTreeProps} from 'antd/es/tree'
import {instance as elementTypeService} from '../../services/ElementTypeService'
import {ElementTypeId} from '../../entities/elementType'
import {Plant} from '../../entities/plant'
import {useRevalidator, useSubmit} from 'react-router-dom'
import {instance as plantsService} from '../../services/PlantsService'
import {useIntl} from 'react-intl'
import {PlantControlRecord} from './PlantControlRecord'
import './CanvasViewEditor.less'
import {ElementControlRecord} from './ElementControlRecord'
import {ToolType} from './toolLayers/CanvasToolManager'
import './CanvasViewEditorData.less'
import {NodeConfig} from 'konva/lib/Node'
import {LinkOutlined} from '@ant-design/icons'
import {FontAwesomeIcon as Icon} from '@fortawesome/react-fontawesome'
import * as IconsRegular from '@fortawesome/free-regular-svg-icons'
import * as Icons from '@fortawesome/free-solid-svg-icons'
import {CanvasViewEditorDataSection} from './CanvasViewEditorDataSection'
import {useGardenPlanVariantParam} from '../../hooks/useGardenPlanVariantParam'

export type PlantWithVisibility = Plant & {isVisible: boolean}
export type ElementWithVisibility = TypedElement & {isVisible: boolean}

export type CanvasViewEditorDataProps = {
  elements: ElementWithVisibility[]
  projectPlants: PlantWithVisibility[]
  currentPlant: {id: number; specimenIndex?: number} | null
  setCurrentPlant: (currentPlant: {id: number; specimenIndex?: number}) => void
  currentElementId: number
  setCurrentElementId: (id: number) => void
  setPlantVisible: (plantId: number, isVisible: boolean) => void
  setElementVisible: (elementId: number, isVisible: boolean) => void
  openPlantById: (id: number) => void
  setTool: (tool: ToolType) => void
  digUpPlant: (diggingPlantId?: number, diggingPlantSpecimenNumber?: number) => void
  plantPlant: (plantingPlantId?: number, plantingPlantSpecimenNumber?: number) => void
  addPlantSpecimen: (plantId: number, newSpecimenCoordinates?: NodeConfig) => void
  removePlantSpecimen: (plantId: number, specimenNumber: number) => void
  connectSolitariusTemplateToPlant: (
    solitariusTemplateElement: ElementWithVisibility,
    plantId: string
  ) => void
  lockedPlantIds?: number[]
}

const {DirectoryTree} = Tree

function filterPlants(
  plants: PlantWithVisibility[],
  type: 'unplantedPlants' | 'separatelyGrowing'
) {
  if (type === 'unplantedPlants') {
    return plants.filter((plant) => {
      if (!plant.coordinates || plant.coordinates.length === 0) {
        return true
      }

      for (let index = 0; index < plant.coordinates.length; index++) {
        if (Object.keys(plant.coordinates[index]).length > 0) {
          return false
        }
      }

      return true
    })
  } else if (type === 'separatelyGrowing') {
    return plants.filter((plant) => {
      if (plant.elementId || !plant.coordinates || plant.coordinates.length === 0) {
        return false
      }

      for (let index = 0; index < plant.coordinates.length; index++) {
        if (Object.keys(plant.coordinates[index]).length > 0) {
          return true
        }
      }

      return false
    })
  }
}

export function CanvasViewEditorData({
  elements,
  projectPlants,
  currentPlant,
  setCurrentPlant,
  currentElementId,
  setCurrentElementId,
  setPlantVisible,
  setElementVisible,
  openPlantById,
  setTool,
  digUpPlant,
  plantPlant,
  addPlantSpecimen,
  removePlantSpecimen,
  connectSolitariusTemplateToPlant,
  lockedPlantIds = [],
}: CanvasViewEditorDataProps) {
  const [messageApi, contextHolder] = message.useMessage()
  const intl = useIntl()

  const [activeKey, setActiveKey] = useState('1')

  const {gardenPlanVariantId} = useGardenPlanVariantParam()
  const unplantedPlants = filterPlants(projectPlants, 'unplantedPlants')
  const unplantedPlantsCount = unplantedPlants.length
  const previousPlantsCount = useRef(projectPlants.length)
  const previousUnplantedPlantsCount = useRef(unplantedPlantsCount)
  const previousGardenPlanVariantId = useRef(gardenPlanVariantId)

  useEffect(() => {
    const isGardenPlanVariantChanged = gardenPlanVariantId !== previousGardenPlanVariantId.current
    const hasUnplantedPlantsInGardenPlanVariant = unplantedPlants.some(
      (plant) => (plant.gardenPlanVariantId ?? null) === gardenPlanVariantId
    )

    if (isGardenPlanVariantChanged && !hasUnplantedPlantsInGardenPlanVariant) {
      setActiveKey('1')
    } else if (projectPlants.length > previousPlantsCount.current) {
      setActiveKey('2')
    } else if (unplantedPlantsCount < previousUnplantedPlantsCount.current) {
      setActiveKey('1')
    }
    previousPlantsCount.current = projectPlants.length
    previousUnplantedPlantsCount.current = unplantedPlantsCount
    previousGardenPlanVariantId.current = gardenPlanVariantId
  }, [projectPlants.length, unplantedPlantsCount, gardenPlanVariantId])

  const [compositionTreeExpandedKeys, setCompositionTreeExpandedKeys] = useState<
    (string | number)[]
  >([1])

  const [unplantedTreeExpandedKeys, setUnplantedTreeExpandedKeys] = useState<(string | number)[]>([
    0,
  ])

  const addPlantSpecimenToCompositionTree: typeof addPlantSpecimen = (
    plantId,
    newSpecimenCoordinates
  ) => {
    setCompositionTreeExpandedKeys((current) => {
      const currentSet = new Set(current)
      currentSet.add(plantId)
      return [...currentSet]
    })
    addPlantSpecimen(plantId, newSpecimenCoordinates)
  }

  const addPlantSpecimenToUnplantedTree: typeof addPlantSpecimen = (
    plantId,
    newSpecimenCoordinates
  ) => {
    setUnplantedTreeExpandedKeys((current) => {
      const currentSet = new Set(current)
      currentSet.add(plantId)
      return [...currentSet]
    })
    addPlantSpecimen(plantId, newSpecimenCoordinates)
  }

  const onSelectElement: DirectoryTreeProps['onSelect'] = (keys, info: any) => {
    if (!info.node.isPlant) {
      const newCurrentElementId = info.node.elementId
      if (newCurrentElementId === currentElementId) {
        setCurrentElementId(null)
      } else {
        setCurrentElementId(newCurrentElementId)
      }
    } else {
      const newCurrentPlantId = info.node.plantId
      const newSpecimenIndex = info.node.specimenIndex
      if (
        newCurrentPlantId === currentPlant?.id &&
        newSpecimenIndex === currentPlant?.specimenIndex
      ) {
        setCurrentPlant(null)
      } else {
        setCurrentPlant({id: newCurrentPlantId, specimenIndex: newSpecimenIndex})
      }
    }
  }
  const onSelectUnplantedPlant: DirectoryTreeProps['onSelect'] = (keys, info: any) => {
    if (!info.node.isPlant) {
      const newCurrentElementId = info.node.elementId //= parseInt(`${keys[0]}`, 10)
      if (newCurrentElementId === currentElementId) {
        setCurrentElementId(null)
      } else {
        setCurrentElementId(newCurrentElementId)
      }
    } else {
      const newCurrentPlantId = info.node.plantId
      const newSpecimenIndex = info.node.specimenIndex
      if (
        newCurrentPlantId === currentPlant?.id &&
        newSpecimenIndex === currentPlant?.specimenIndex
      ) {
        setCurrentPlant(null)
      } else {
        setCurrentPlant({id: newCurrentPlantId, specimenIndex: newSpecimenIndex})
      }
    }
  }

  function plantsTreeDataGenerator(
    plants: PlantWithVisibility[],
    setPlantVisible: CanvasViewEditorDataProps['setPlantVisible'],
    openPlantById: CanvasViewEditorDataProps['openPlantById'],
    setTool: CanvasViewEditorDataProps['setTool'],
    digUpPlant: CanvasViewEditorDataProps['digUpPlant'],
    plantPlant: CanvasViewEditorDataProps['plantPlant'],
    addPlantSpecimen: CanvasViewEditorDataProps['addPlantSpecimen'],
    removePlantSpecimen: CanvasViewEditorDataProps['removePlantSpecimen']
  ) {
    const sortedPlants = [...plants].sort(
      (a, b) => Number(!!b.gardenPlanVariantId) - Number(!!a.gardenPlanVariantId)
    )

    const treePlants = sortedPlants.map((plant) => {
      const numberOfPlantedSpecimens = plant.coordinates
        .map((coords) => Object.keys(coords).length)
        .filter((a) => a > 0).length

      const numberOfSpecimens = plant.coordinates.length

      const isPlantPlanted =
        plant.coordinates.length >= 1 &&
        plant.coordinates.map((coords) => Object.keys(coords).length).some((a) => a > 0)

      return {
        key: `1-${plant.id}`,
        title: (
          <PlantControlRecord
            plantName={`${plant.name} 
            ${
              isPlantPlanted &&
              numberOfSpecimens === 1 &&
              numberOfSpecimens === numberOfPlantedSpecimens
                ? ``
                : ``
            }
            ${
              isPlantPlanted &&
              numberOfSpecimens > 1 &&
              numberOfSpecimens === numberOfPlantedSpecimens
                ? `(${numberOfSpecimens})`
                : ``
            }
            ${
              isPlantPlanted &&
              numberOfSpecimens > 1 &&
              numberOfSpecimens !== numberOfPlantedSpecimens
                ? `(${intl.formatMessage({
                    id: `planted`,
                  })} ${numberOfPlantedSpecimens} ${intl.formatMessage({
                    id: `from2`,
                  })} ${numberOfSpecimens})`
                : ``
            }
            ${!isPlantPlanted && numberOfSpecimens <= 1 ? `` : ``}
            ${
              !isPlantPlanted && numberOfSpecimens > 1
                ? `(${intl.formatMessage({
                    id: `notPlanted`,
                  })} ${numberOfSpecimens})`
                : ``
            }
            `}
            isPlanted={isPlantPlanted}
            isSingle={plant.coordinates.length <= 1}
            isSinglePlanted={plant.coordinates.length <= 1 && isPlantPlanted}
            isFromGardenPlan={!!plant.gardenPlanVariantId}
            isLocked={lockedPlantIds.includes(plant.id)}
            isSpecimen={false}
            specimenNumber={null}
            isVisible={plant.isVisible}
            plantId={plant.id}
            setVisibility={(isVisible) => {
              setPlantVisible(plant.id, isVisible)
            }}
            openPlantById={openPlantById}
            setTool={setTool}
            digUpPlant={digUpPlant}
            plantPlant={plantPlant}
            addPlantSpecimen={addPlantSpecimen}
          />
        ),

        isLeaf: plant.coordinates.length <= 1 ? true : false,
        isPlant: true,
        plantId: plant.id,
        icon: (
          <img
            src={
              elementTypeService.getElementType('plant').templates[
                elementTypeService.chooseElementTypeTemplate('plant', plant.template)
              ].src
              // ? elementTypeService.getElementType('plant').templates[
              //     plant.template ? plant.template : 'tree'
              //   ].src
            }
            width={20}
            height={20}
            style={{display: 'inline-flex'}}
          ></img>
        ),
        children:
          plant.coordinates.length <= 1
            ? null
            : plant.coordinates.map((plantSpecimen, index) => ({
                key: `${plant.id}-${index}`,
                title: (
                  <PlantControlRecord
                    plantName={`${plant.name} ${
                      Object.keys(plantSpecimen).length > 0
                        ? ``
                        : `(${intl.formatMessage({
                            id: `notPlanted`,
                          })})`
                    }`}
                    isPlanted={Object.keys(plantSpecimen).length === 0 ? false : true}
                    isSingle={false}
                    isSinglePlanted={
                      Object.keys(plantSpecimen).length === 0
                        ? false
                        : true && numberOfPlantedSpecimens === 1
                    }
                    isSpecimen={true}
                    isFromGardenPlan={!!plant.gardenPlanVariantId}
                    isLocked={lockedPlantIds.includes(plant.id)}
                    isVisible={true}
                    plantId={plant.id}
                    setVisibility={() => {}}
                    openPlantById={openPlantById}
                    setTool={setTool}
                    digUpPlant={digUpPlant}
                    plantPlant={plantPlant}
                    specimenNumber={index}
                    removePlantSpecimen={removePlantSpecimen}
                  />
                ),
                isLeaf: true,
                isPlant: true,
                plantId: plant.id,
                specimenIndex: index,
                icon: (
                  <img
                    src={
                      elementTypeService.getElementType('plant').templates[
                        elementTypeService.chooseElementTypeTemplate('plant', plant.template)
                      ].src
                    }
                    width={20}
                    height={20}
                    style={{display: 'inline-flex'}}
                  ></img>
                ),
              })),
      }
    })

    return treePlants
  }

  //const treeDataElements: DataNode[] = []
  const treeDataElementsSeparatelyGrowing: DataNode[] = []
  const treeDataElementsSOlitariusTemplates: DataNode[] = []
  const treeDataElementsOther: DataNode[] = []
  const treeUnplantedDataElements: DataNode[] = []
  const elementsPerType = new Map<ElementTypeId, ElementWithVisibility[]>()
  const elementsPerSolitariusType = new Map<ElementTypeId, ElementWithVisibility[]>()
  const solitariusTemplateTypes = elementTypeService.getSolitariusElementTypes()

  for (const element of elements) {
    const elementType = element.type
    const elementTypesArray =
      elementsPerType.get(elementType) || elementsPerSolitariusType.get(elementType) || []
    elementTypesArray.push(element)
    solitariusTemplateTypes.some((t) => t === elementType)
      ? elementsPerSolitariusType.set(elementType, elementTypesArray)
      : elementsPerType.set(elementType, elementTypesArray)
  }

  // treeDataElementsSeparatelyGrowing.push({
  //   key: 1,
  //   title: intl.formatMessage({id: 'separatelyGrowing'}),
  //   children: plantsTreeDataGenerator(
  //     // projectPlants.filter(
  //     //   (plant) => !plant.elementId && plant.coordinates && plant.coordinates.length > 0
  //     // ),
  //     filterPlants(projectPlants, 'separatelyGrowing'),
  //     setPlantVisible,
  //     openPlantById,
  //     setTool,
  //     digUpPlant,
  //     plantPlant,
  //     addPlantSpecimenToCompositionTree,
  //     removePlantSpecimen
  //   ),
  // })
  //const separatelyGrowingPlants = filterPlants(projectPlants, 'separatelyGrowing')
  for (const plant of plantsTreeDataGenerator(
    filterPlants(projectPlants, 'separatelyGrowing'),
    setPlantVisible,
    openPlantById,
    setTool,
    digUpPlant,
    plantPlant,
    addPlantSpecimenToCompositionTree,
    removePlantSpecimen
  )) {
    treeDataElementsSeparatelyGrowing.push(plant)
  }

  for (const elementTypeId of elementsPerSolitariusType.keys()) {
    const elementType = elementTypeService.getElementType(elementTypeId)
    treeDataElementsSOlitariusTemplates.push({
      key: elementTypeId,
      title: (
        <span style={{color: 'green'}}>
          {intl.formatMessage({id: `${elementType.displayName}Plural`})}
        </span>
      ),

      children: elementsPerSolitariusType.get(elementTypeId).map((element) => ({
        key: element.elementId,
        elementId: element.elementId,
        title: (
          <ElementControlRecord
            elementName={element.name === '' ? intl.formatMessage({id: `noTitle`}) : element.name}
            elementId={element.elementId}
            isVisible={element.isVisible}
            setVisibility={(isVisible) => {
              setElementVisible(element.elementId, isVisible)
            }}
            isSolitariusTemplate={true}
            solitariusTemplateElement={element}
            plantsList={projectPlants}
            elementsList={elements}
            onConnectPlant={connectSolitariusTemplateToPlant}
          ></ElementControlRecord>
        ),
        isPlant: false,
        children: plantsTreeDataGenerator(
          projectPlants.filter((plant) => plant.elementId === element.elementId),
          setPlantVisible,
          openPlantById,
          setTool,
          digUpPlant,
          plantPlant,
          addPlantSpecimenToCompositionTree,
          removePlantSpecimen
        ),
      })),
    })
  }

  for (const elementTypeId of elementsPerType.keys()) {
    const elementType = elementTypeService.getElementType(elementTypeId)
    treeDataElementsOther.push({
      key: elementTypeId,
      title: intl.formatMessage({id: `${elementType.displayName}Plural`}),
      children: elementsPerType.get(elementTypeId).map((element) => ({
        key: element.elementId,
        elementId: element.elementId,
        title: (
          <ElementControlRecord
            elementName={element.name === '' ? intl.formatMessage({id: `noTitle`}) : element.name}
            elementId={element.elementId}
            isVisible={element.isVisible}
            setVisibility={(isVisible) => {
              setElementVisible(element.elementId, isVisible)
            }}
            isSolitariusTemplate={false}
          ></ElementControlRecord>
        ),
        isPlant: false,
        children: plantsTreeDataGenerator(
          projectPlants.filter((plant) => plant.elementId === element.elementId),
          setPlantVisible,
          openPlantById,
          setTool,
          digUpPlant,
          plantPlant,
          addPlantSpecimenToCompositionTree,
          removePlantSpecimen
        ),
      })),
    })
  }

  treeUnplantedDataElements.push({
    key: 0,
    title: intl.formatMessage({id: 'unplantedPlants'}),
    children: plantsTreeDataGenerator(
      filterPlants(projectPlants, 'unplantedPlants'),
      setPlantVisible,
      openPlantById,
      setTool,
      digUpPlant,
      plantPlant,
      addPlantSpecimenToUnplantedTree,
      removePlantSpecimen
    ),
  })

  const treeDataPlants: Plant[] = []

  for (const plant of projectPlants) {
    if (plant.elementId === currentElementId || currentElementId === null) {
      treeDataPlants.push(plant)
    }
  }

  const elementKey = currentElementId
  const plantKey = currentPlant?.id
  const plantSpecimenKey =
    plantKey &&
    typeof currentPlant?.specimenIndex === 'number' &&
    `${plantKey}-${currentPlant?.specimenIndex}`

  const selectedKeys: (number | string)[] = [elementKey]
  if (plantKey) {
    selectedKeys.push(plantKey)

    if (plantSpecimenKey) {
      selectedKeys.push(plantSpecimenKey)
    }
  }

  return (
    <>
      {contextHolder}
      <Tabs
        onChange={(key) => setActiveKey(key)}
        type="card"
        className="canvas-data"
        activeKey={activeKey}
        items={[
          {
            key: '1',
            label: intl.formatMessage({id: 'projectComposition'}),
            children: (
              <>
                {/* <div className="canvas-data__section canvas-data__section--planted-plants">
                  <div className="canvas-data__section__icon">
                    <Icon icon={Icons.faSunPlantWilt} />
                  </div>
                  <div className="canvas-data__section__data">
                    <div className="canvas-data__section__name">
                      {intl.formatMessage({id: `plantedPlants`})}
                    </div>
                    <div className="canvas-data__section__description">
                      ({intl.formatMessage({id: `canvasDataDescriptionSeparatlyGrowing`})})
                    </div>
                    {treeDataElementsSeparatelyGrowing.length > 0 ? (
                      <Tree
                        multiple
                        defaultExpandAll={false}
                        onSelect={onSelectElement}
                        expandedKeys={compositionTreeExpandedKeys}
                        onExpand={(keys, _info) => setCompositionTreeExpandedKeys(keys)}
                        selectedKeys={selectedKeys}
                        treeData={treeDataElementsSeparatelyGrowing}
                        showIcon
                        className="elements-tree"
                      />
                    ) : (
                      <span>{intl.formatMessage({id: `noEntries`})}</span>
                    )}
                  </div>
                </div> */}
                <CanvasViewEditorDataSection
                  treeDataElements={treeDataElementsSeparatelyGrowing}
                  onSelectElement={onSelectElement}
                  compositionTreeExpandedKeys={compositionTreeExpandedKeys}
                  setCompositionTreeExpandedKeys={setCompositionTreeExpandedKeys}
                  selectedKeys={selectedKeys}
                  sectionType="planted-plants"
                  name="plantedPlants"
                  description="canvasDataDescriptionSeparatlyGrowing"
                  icon={Icons.faSunPlantWilt}
                />
                <CanvasViewEditorDataSection
                  treeDataElements={treeDataElementsSOlitariusTemplates}
                  onSelectElement={onSelectElement}
                  compositionTreeExpandedKeys={compositionTreeExpandedKeys}
                  setCompositionTreeExpandedKeys={setCompositionTreeExpandedKeys}
                  selectedKeys={selectedKeys}
                  sectionType="solitarius-templates"
                  name="plantModelWithoutPlantRecord"
                  description="canvasDataDescriptionSolitariusTemplates"
                  icon={Icons.faSeedling}
                />
                <CanvasViewEditorDataSection
                  treeDataElements={treeDataElementsOther}
                  onSelectElement={onSelectElement}
                  compositionTreeExpandedKeys={compositionTreeExpandedKeys}
                  setCompositionTreeExpandedKeys={setCompositionTreeExpandedKeys}
                  selectedKeys={selectedKeys}
                  sectionType="other-elements"
                  name="otherSiteElements"
                  description={null}
                  icon={Icons.faToriiGate}
                />
                {/* <div className="canvas-data__section plant-model-without-record">
                  <div className="canvas-data__section__name">
                    {intl.formatMessage({id: `plantModelWithoutPlantRecord`})}
                  </div>
                  <div className="canvas-data__section__description">
                    ({intl.formatMessage({id: `canvasDataDescriptionSolitariusTemplates`})}
                    <LinkOutlined />)
                  </div>
                  {treeDataElementsSOlitariusTemplates.length > 0 ? (
                    <Tree
                      // style={{
                      //   overflow: 'hidden',
                      //   overflowY: 'scroll',
                      // }}
                      multiple
                      defaultExpandAll={false}
                      onSelect={onSelectElement}
                      expandedKeys={compositionTreeExpandedKeys}
                      onExpand={(keys, _info) => setCompositionTreeExpandedKeys(keys)}
                      selectedKeys={selectedKeys}
                      treeData={treeDataElementsSOlitariusTemplates}
                      showIcon
                      className="elements-tree"
                    />
                  ) : (
                    <span>{intl.formatMessage({id: `noEntries`})}</span>
                  )}
                </div>
                <div className="canvas-data__section">
                  <div className="canvas-data__section__name">
                    {intl.formatMessage({id: `otherSiteElements`})}
                  </div>
                  {treeDataElementsOther.length > 0 ? (
                    <Tree
                      // style={{
                      //   overflow: 'hidden',
                      //   overflowY: 'scroll',
                      // }}
                      multiple
                      defaultExpandAll={false}
                      onSelect={onSelectElement}
                      expandedKeys={compositionTreeExpandedKeys}
                      onExpand={(keys, _info) => setCompositionTreeExpandedKeys(keys)}
                      selectedKeys={selectedKeys}
                      treeData={treeDataElementsOther}
                      showIcon
                      className="elements-tree"
                    />
                  ) : (
                    <span>{intl.formatMessage({id: `noEntries`})}</span>
                  )}
                </div> */}
              </>
            ),
          },
          {
            key: '2',
            label: (
              <span style={{display: 'flex', alignItems: 'center'}}>
                <span style={{flex: '0 1 auto', textWrap: 'wrap'}}>
                  {intl.formatMessage({id: 'unplantedPlants'})}
                </span>
                <Badge
                  count={treeUnplantedDataElements[0].children.length}
                  style={{marginLeft: '5px', backgroundColor: '#f58153'}}
                />
              </span>
            ),
            children: (
              <Tree
                // style={{
                //   overflow: 'hidden',
                //   overflowY: 'scroll',
                // }}
                multiple
                onSelect={onSelectUnplantedPlant} // TODO: использовать другую функцию, потому что другое дерево
                expandedKeys={unplantedTreeExpandedKeys}
                onExpand={(keys, _info) => setUnplantedTreeExpandedKeys(keys)}
                selectedKeys={selectedKeys}
                treeData={treeUnplantedDataElements}
                showIcon
                className="elements-tree"
              />
            ),
          },
        ]}
      />
    </>
  )
}
